import { useState, useEffect, useCallback } from 'react';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { Server, RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Loader2, Database, Wrench, Globe } from 'lucide-react';
import { toast } from 'sonner';

const STATUS = {
  ok:             { dot: '#34D399', badge: 'ops-badge-success', Icon: CheckCircle2, label: 'Operational' },
  degraded:       { dot: '#FBBF24', badge: 'ops-badge-warning', Icon: AlertTriangle, label: 'Degraded' },
  down:           { dot: '#F87171', badge: 'ops-badge-danger', Icon: XCircle, label: 'Down' },
  not_configured: { dot: '#52525B', badge: 'ops-badge-muted', Icon: MinusCircle, label: 'Not configured' },
};

export default function OpsSystemPage() {
  const { opsAxios, OPS_API } = useOpsAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dh, setDh] = useState(null);
  const [dhLoading, setDhLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [regions, setRegions] = useState(null);
  const [regionsLoading, setRegionsLoading] = useState(true);

  const loadRegions = useCallback(async () => {
    setRegionsLoading(true);
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/system/regions-health` });
      setRegions(res.data);
    } catch (e) { console.error(e); }
    finally { setRegionsLoading(false); }
  }, [opsAxios, OPS_API]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/system/health` });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [opsAxios, OPS_API]);

  const loadDataHealth = useCallback(async () => {
    setDhLoading(true);
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/system/data-health` });
      setDh(res.data);
    } catch (e) { console.error(e); }
    finally { setDhLoading(false); }
  }, [opsAxios, OPS_API]);

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const res = await opsAxios({ method: 'POST', url: `${OPS_API}/system/data-health/cleanup` });
      const rc = res.data.removed_contacts ?? res.data.removed ?? 0;
      const rt = res.data.removed_tasks ?? 0;
      toast.success(`Removed ${rc} stray contact${rc === 1 ? '' : 's'} · ${rt} orphaned task${rt === 1 ? '' : 's'}`);
      await loadDataHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed (needs admin role)');
    } finally { setCleaning(false); }
  };

  useEffect(() => { load(); loadDataHealth(); loadRegions(); }, [load, loadDataHealth, loadRegions]);

  const overall = data?.overall;
  const ov = STATUS[overall] || STATUS.not_configured;

  return (
    <div data-testid="ops-system-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="ops-h1 flex items-center gap-2"><Server size={18} style={{ color: 'var(--ops-text-2)' }} />System &amp; API Health</h1>
          <p className="ops-sub mt-1">Live status of every integration the app depends on</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="ops-health-recheck" className="ops-btn ops-btn-secondary h-9 px-3.5">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-check
        </button>
      </div>

      {data && (
        <div className="ops-card flex items-center gap-3 px-5 py-4 mb-5">
          <span className={`ops-dot ${overall === 'ok' ? 'ops-ping' : ''}`} style={{ background: ov.dot, color: ov.dot }} />
          <div>
            <p className="ops-h2" data-testid="ops-health-overall" style={{ color: ov.dot }}>
              {overall === 'ok' ? 'All systems operational' : overall === 'degraded' ? 'Partial degradation' : 'Outage detected'}
            </p>
            <p className="ops-mono text-[11px] mt-0.5" style={{ color: 'var(--ops-muted)' }}>
              {data.summary.ok} operational · {data.summary.degraded} degraded · {data.summary.down} down · {data.summary.not_configured} not configured
            </p>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ops-health-checks">
        {loading && !data ? Array(7).fill(0).map((_, i) => <div key={i} className="ops-skel h-24" />)
          : (data?.checks || []).map(c => {
            const s = STATUS[c.status] || STATUS.not_configured;
            return (
              <div key={c.name} className="ops-card ops-card-hover p-4" data-testid={`health-${c.name}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="ops-dot" style={{ background: s.dot }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--ops-text)' }}>{c.name}</p>
                  </div>
                  <span className={`ops-badge ${s.badge}`}><s.Icon size={11} />{s.label}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] ops-mono" style={{ color: 'var(--ops-muted)' }}>
                  <span>{c.detail}</span>
                  <span style={{ color: 'var(--ops-text-2)' }}>{c.latency_ms}ms</span>
                </div>
              </div>
            );
          })}
      </div>

      {/* Data residency clusters */}
      <div className="flex items-center justify-between mt-8 mb-4">
        <div>
          <h2 className="ops-h2 flex items-center gap-2"><Globe size={16} style={{ color: 'var(--ops-text-2)' }} />Data residency clusters</h2>
          <p className="ops-sub mt-1">
            Live connectivity to each regional database and the ops database ·
            routing {regions?.routing_enabled ? <span style={{ color: '#34D399' }}>ENABLED</span> : <span style={{ color: 'var(--ops-muted)' }}>disabled (single DB)</span>}
          </p>
        </div>
        <button onClick={loadRegions} disabled={regionsLoading} data-testid="ops-regions-recheck" className="ops-btn ops-btn-secondary h-9 px-3.5">
          {regionsLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-check
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ops-regions-health">
        {regionsLoading && !regions ? Array(4).fill(0).map((_, i) => <div key={i} className="ops-skel h-24" />)
          : (regions?.clusters || []).map(c => {
            const s = STATUS[c.status] || STATUS.not_configured;
            return (
              <div key={c.label} className="ops-card p-4" data-testid={`region-health-${c.label}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="ops-dot" style={{ background: s.dot }} />
                    <p className="ops-mono text-sm font-medium" style={{ color: 'var(--ops-text)' }}>{c.label}</p>
                  </div>
                  <span className={`ops-badge ${s.badge}`}><s.Icon size={11} />{s.label}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] ops-mono" style={{ color: 'var(--ops-muted)' }}>
                  <span>{c.error ? c.error : (c.db || (c.status === 'not_configured' ? 'no env var' : '—'))}</span>
                  {c.latency_ms != null && <span style={{ color: 'var(--ops-text-2)' }}>{c.latency_ms}ms</span>}
                </div>
              </div>
            );
          })}
      </div>

      {/* Data health */}
      <div className="flex items-center justify-between mt-8 mb-4">
        <div>
          <h2 className="ops-h2 flex items-center gap-2"><Database size={16} style={{ color: 'var(--ops-text-2)' }} />Data health</h2>
          <p className="ops-sub mt-1">Data-quality signals across cards, tasks and contacts</p>
        </div>
        {dh?.metrics?.some(m => m.cleanable && m.count > 0) && (
          <button onClick={runCleanup} disabled={cleaning} data-testid="ops-data-cleanup" className="ops-btn ops-btn-secondary h-9 px-3.5">
            {cleaning ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />} Run cleanup
          </button>
        )}
      </div>

      {dh?.healthy && !dhLoading && (
        <div className="ops-card flex items-center gap-3 px-5 py-4 mb-5" data-testid="ops-data-health-ok">
          <span className="ops-dot ops-ping" style={{ background: '#34D399', color: '#34D399' }} />
          <p className="ops-h2" style={{ color: '#34D399' }}>No data-quality issues detected</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ops-data-health">
        {dhLoading && !dh ? Array(3).fill(0).map((_, i) => <div key={i} className="ops-skel h-24" />)
          : (dh?.metrics || []).map(m => {
            const bad = m.count > 0;
            return (
              <div key={m.key} className="ops-card p-4" data-testid={`data-health-${m.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium" style={{ color: 'var(--ops-text)' }}>{m.label}</p>
                  <span className={`ops-badge ${bad ? 'ops-badge-warning' : 'ops-badge-success'}`}>
                    {bad ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />}{bad ? 'Attention' : 'Clean'}
                  </span>
                </div>
                <p className="ops-h1" data-testid={`data-health-count-${m.key}`} style={{ color: bad ? '#FBBF24' : 'var(--ops-text)' }}>{m.count}</p>
                <p className="text-[11px] mt-1" style={{ color: 'var(--ops-muted)' }}>{m.hint}{m.cleanable && bad ? ' · fixable' : ''}</p>
              </div>
            );
          })}
      </div>
      {dh?.last_cleanup?.ran_at && (
        <p className="ops-mono text-[11px] mt-3" style={{ color: 'var(--ops-muted)' }} data-testid="ops-last-cleanup">
          Last cleanup: removed {dh.last_cleanup.removed} · {new Date(dh.last_cleanup.ran_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
